/**
 * Authenticated API Service for AIMetaPic V2
 * Handles authenticated API calls following AUTH_SYSTEM_STANDARDIZATION_GUIDE.md
 * 
 * Uses AuthCore as single source of truth for authentication
 */

import { AuthCore } from '../core/AuthCore';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp: string;
  requestId: string;
}

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
  timeout?: number;
}

class AuthenticatedApiService {
  private readonly baseUrl: string;
  private readonly defaultTimeout = 30000; // 30 seconds

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
  }

  /**
   * Validate authentication and get token
   * Following AUTH_SYSTEM_STANDARDIZATION_GUIDE.md pattern
   */
  private async validateAuthentication(): Promise<string> {
    const authState = AuthCore.getInstance().getState();
    if (!authState.isAuthenticated) {
      throw new Error('User not authenticated');
    }
    
    const token = await AuthCore.getInstance().getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }
    
    return token;
  }

  /**
   * Make authenticated API request
   * Following service layer pattern from AUTH_SYSTEM_STANDARDIZATION_GUIDE.md
   */
  private async makeRequest<T>(
    endpoint: string, 
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    try {
      // Validate authentication and get token
      const token = await this.validateAuthentication();
      
      const {
        method = 'GET',
        body,
        headers = {},
        timeout = this.defaultTimeout
      } = config;

      // Prepare request headers
      const requestHeaders: HeadersInit = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...headers
      };

      // Prepare request options
      const requestOptions: RequestInit = {
        method,
        headers: requestHeaders,
        ...(body && { body: JSON.stringify(body) })
      };

      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), timeout);
      });

      // Make request with timeout
      const fullUrl = `${this.baseUrl}${endpoint}`;
      
      const response = await Promise.race([
        fetch(fullUrl, requestOptions),
        timeoutPromise
      ]);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: ApiResponse<T> = await response.json();
      return result;

    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      
      // Return standardized error response
      return {
        success: false,
        error: {
          code: 'API_REQUEST_FAILED',
          message: error instanceof Error ? error.message : 'Unknown error',
          details: { endpoint, method: config.method || 'GET' }
        },
        timestamp: new Date().toISOString(),
        requestId: 'client-' + Math.random().toString(36).substring(7)
      };
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return AuthCore.getInstance().getState().isAuthenticated;
  }

  /**
   * Get current user info from AuthCore
   */
  getCurrentUser() {
    return AuthCore.getInstance().getState().user;
  }
}

// Export singleton instance
export const authenticatedApiService = new AuthenticatedApiService();
export default authenticatedApiService;
